/**
 * Doom's Update 01 PEN-09: everything the Value Dashboard's game rows need about the caller's Penelope games, in ONE
 * poll (GET /api/penelope/games/mine) shared by every row — never one read per row. Refcounted: the first row that
 * mounts starts the poll, the last one to unmount stops it; a hidden tab skips its reads.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { apiFetch } from '@/stores/authStore';
import type { PenelopeLive, PenelopeRest, PenelopeRegime } from './usePenelope';

const POLL_MS = 2_000;

/** What a game's last STOP did with her real orders (relay services/penelope PenelopeStopState). */
export interface PenelopeStopState {
  reason: string;
  status: 'stopping' | 'cancels-pending' | 'clean' | 'unconfirmed';
  requestedAtMs: number;
  exitedAtMs: number | null;
  restingAtStop: number;
  cancelsSent: number;
  cancelErrors: number;
  swept: number;
  sweepFailed: boolean;
  unknownAtExit: number;
  lastCheckAtMs: number | null;
  openAtLastCheck: number | null;
  followUpCancels: number;
}
export interface PenelopeMineGame {
  id: string;
  agentId: string;
  eventTicker: string;
  gameKey: string | null;
  labelA: string | null;
  labelB: string | null;
  armed: boolean;
  mode: 'live' | 'shadow';
  sides: { A: boolean; B: boolean };
  parked: string | null;
  stop: PenelopeStopState | null;
  running: {
    shadow: boolean; phase: string; regime: PenelopeRegime; gate: string | null; net: number;
    inv: { A: { contracts: number }; B: { contracts: number } }; rests: { A: PenelopeRest | null; B: PenelopeRest | null }; pnlCents: number;
    /** Relay ≥ 2026-10-20 (PR-063): the snapshot fields the status sentence reads, so the row says what the ticket says. */
    status?: Partial<PenelopeLive['baseline']>;
  } | null;
  pnlCents: number;
  fillsCount: number;
  /** Relay ≥ 2026-10-20 (R9): 'auto' when auto-start attached the row. */
  origin?: 'auto' | 'manual';
}
/** The caller's other strategy rows on an event (R9): the mini game and the harvester, for the row's chips. */
export interface PenelopeMineOther { id: string; strategy: 'penelope' | 'minigame' | 'harvester'; armed: boolean; mode: string; regime: string | null; origin: 'auto' | 'manual' }
export interface PenelopeMine {
  games: Record<string, PenelopeMineGame>;
  /** Relay ≥ 2026-10-20 (R9): the caller's mini game and harvester rows per event. */
  others?: Record<string, PenelopeMineOther[]>;
  lastStopped: Record<string, { stopReason: string | null; at: string; stop: PenelopeStopState | null }>;
  /** The all-sports default (as before). */
  profile: { startMode: 'live' | 'shadow'; version: number } | null;
  /** Every saved default by sport (relay ≥ 2026-10-14): a row starts from its sport's, else `profile`. */
  profiles?: Record<string, { startMode: 'live' | 'shadow'; version: number }>;
  enabled: boolean;
  eligible: boolean;
  liveEligible: boolean;
  slots: { armedByCaller: number; maxArmedPerTrader: number; maxArmed: number };
  serverTs: number;
}

interface MineStore { data: PenelopeMine | null; error: string | null }
export const usePenelopeMineStore = create<MineStore>(() => ({ data: null, error: null }));

let subscribers = 0;
let timer: ReturnType<typeof setInterval> | null = null;
let inflight: Promise<void> | null = null;

async function read(): Promise<void> {
  try {
    const res = await apiFetch('/api/penelope/games/mine');
    const body = (await res.json().catch(() => ({}))) as PenelopeMine & { error?: string };
    if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
    usePenelopeMineStore.setState({ data: body, error: null });
  } catch (e) {
    usePenelopeMineStore.setState({ error: (e as Error).message });
  }
}

/** Re-read now (after a Start, a STOP, a saved default); one read in flight at a time. */
export function refreshPenelopeMine(): Promise<void> {
  if (subscribers === 0) return Promise.resolve();
  inflight ??= read().finally(() => { inflight = null; });
  return inflight;
}

function start(): void {
  if (timer) return;
  void refreshPenelopeMine();
  timer = setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    void refreshPenelopeMine();
  }, POLL_MS);
}
function stop(): void {
  if (timer) clearInterval(timer);
  timer = null;
}

/** Keep the shared poll running while this component is mounted (and `enabled`). */
export function usePenelopeMine(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return undefined;
    subscribers += 1;
    start();
    return () => {
      subscribers -= 1;
      if (subscribers === 0) stop();
    };
  }, [enabled]);
}

/** Test seam. */
export function resetPenelopeMine(): void {
  stop();
  subscribers = 0;
  inflight = null;
  usePenelopeMineStore.setState({ data: null, error: null });
}
